import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Form, Modal } from "reactstrap";
import CustomeButton from "../../Forms/CustomeButton/CustomeButton";
import { useDispatch, useSelector } from "react-redux";
import { capitalCaseLabel, responseToaster } from "../../../../helperFunctions";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import ControlledDropDown from "../../Forms/Controller/ControlledDropDown";
import { Filter_Data_Transaction } from "../../../../redux/slices/transactionSlice";
import { AddBlockInfo } from "../../../../redux/slices/blockInfoSlice";
import ControlledInput from "../../Forms/Controller/ControlledInput";
import { DROPDOWN_ALL_VALUE, showToaster } from "../../../../constant";
import { prepareFieldName } from "../../../../pages/Support/BlockInfo/BlockInfo";
import { citiesGet, stateGet } from "../../../../redux/slices/authSlice";

const BlockInfoModal = (props) => {
  const { isModalOpen, onCloseModal, onGet } = props;
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [ddLoading, setDdLoading] = useState(false);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [selectedState, setSelectedState] = useState(null);

  const { transactionFilterState } = useSelector((state) => state?.transaction);
  const { stateLoading, citiesLoading } = useSelector((state) => state?.auth);
  const getFilterTransactions = async () => {
    try {
      setDdLoading(true);
      await dispatch(Filter_Data_Transaction()).unwrap();
      setDdLoading(false);
    } catch (err) {
      setDdLoading(false);
    }
  };
  const schema = Yup.object({
    merchantId: Yup.string().required("Please select merchant."),
    ...(isModalOpen !== "block-city-state" && {
      columnName: Yup.string().required("Please add column name."),
      blockData: Yup.string().required("Please add block data."),
    }),
  });

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    setValue,
    resetField,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onClose = () => {
    onCloseModal();
    setIsLoading(false);
    setDdLoading(false);
    setCityOptions([]);
    setSelectedState(null);
    reset();
  };

  const onSubmit = async () => {
    try {
      const values = getValues();
      if (
        !values?.cust_city &&
        !values?.cust_state &&
        isModalOpen === "block-city-state"
      ) {
        return showToaster("Please select either City or State", "Error");
      }
      setIsLoading(true);

      const payload = {
        merchantId: values?.merchantId,
        columnName: values?.columnName,
        blockData: values?.blockData,
      };

      if (isModalOpen === "block-city-state") {
        if (values?.cust_city) {
          payload.columnName = "cust_city";
          payload.blockData = values?.cust_city;
        }
        if (!values?.cust_city && values?.cust_state) {
          payload.columnName = "cust_state";
          payload.blockData = values?.cust_state;
        }
      }

      const res = await dispatch(AddBlockInfo(payload)).unwrap();
      if (res) {
        await onGet();
        responseToaster(res);
        onClose();
      }
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
    }
  };

  const prepareMerchantOption = () => {
    const options = transactionFilterState?.cilent_data
      ?.slice()
      ?.sort((a, b) => a?.merchantName?.localeCompare?.(b?.merchantName))
      ?.map((val) => {
        const clientDataOption = {
          value: val?.merchantId,
          label: val?.merchantName,
        };
        return clientDataOption;
      });
    return options;
  };

  const prepareField = () => {
    return prepareFieldName()?.filter?.(
      (name) => name?.value !== "cust_state" && name?.value !== "cust_city"
    );
  };

  const getStates = async () => {
    try {
      const res = await dispatch(stateGet({})).unwrap();
      const formatted = res?.data?.map?.((s) => ({
        label: s?.name,
        value: s?.id,
      }));
      setStateOptions(formatted || []);
    } catch (err) {
      console.error("Error fetching states:", err);
    }
  };

  const getCities = async (state_id) => {
    try {
      const res = await dispatch(citiesGet({ state_id })).unwrap();
      const formatted = res?.data?.map?.((c) => ({
        label: c?.name,
        value: c?.name,
      }));
      setCityOptions(formatted || []);
    } catch (err) {
      console.error("Error fetching cities:", err);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      getFilterTransactions();
      getStates();
    }
  }, [isModalOpen]);

  if (!isModalOpen) {
    return;
  }

  return (
    <Modal isOpen={isModalOpen} toggle={onClose} centered={true}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className="modal-header">
          <h3 className="modal-title mt-0">
            Add Block Info{" "}
            {isModalOpen === "block-city-state" ? "City/State" : null}
          </h3>
          <i onClick={onClose} className="fa-solid fa-xmark"></i>
        </div>
        <div className="modal-body">
          <ControlledDropDown
            id="merchantId"
            name="merchantId"
            label="Merchant Name"
            isLoading={ddLoading}
            isDefaultOption={DROPDOWN_ALL_VALUE}
            errors={errors}
            isDisabled={ddLoading}
            options={prepareMerchantOption()}
            control={control}
          />
          {isModalOpen === "block-city-state" ? (
            <>
              <ControlledDropDown
                id="cust_state"
                name="cust_state"
                label="Customer State"
                errors={errors}
                value={selectedState}
                options={stateOptions}
                isLoading={stateLoading}
                isDisabled={stateLoading}
                control={control}
                onChange={(e) => {
                  setSelectedState(e?.value);
                  getCities(e?.value);
                  setValue("cust_state", e?.label);
                  resetField("cust_city");
                }}
              />
              <ControlledDropDown
                id="cust_city"
                name="cust_city"
                label="Customer City"
                isLoading={citiesLoading}
                isDisabled={citiesLoading}
                errors={errors}
                options={cityOptions}
                control={control}
              />
            </>
          ) : (
            <>
              <ControlledDropDown
                id="columnName"
                name="columnName"
                label="Column Name"
                errors={errors}
                options={prepareField()}
                control={control}
              />
              <ControlledInput
                name="blockData"
                label="Block Data"
                placeholder={"Enter Block Data"}
                control={control}
                errors={errors}
              />
            </>
          )}
        </div>
        <div className="modal-footer">
          <div className="d-flex justify-content-end button-space">
            <Button type="button" className="graybutton" onClick={onClose}>
              Cancel
            </Button>
            <CustomeButton
              type="submit"
              isButtonLoading={isLoading}
              className="submitbutton"
            >
              Submit
            </CustomeButton>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default BlockInfoModal;
